import { useEffect, useState, type ReactNode } from 'react';
import { IconLeft } from '../components/icons';
import MobileApprovalActionIcon from '../components/MobileApprovalActionIcon';
import MobileAttachment from '../components/MobileAttachment';
import MobileTag from '../components/MobileTag';
import MobileUserChip from '../components/MobileUserChip';
import type { MobileTask } from '../types';

type Props = {
  task: MobileTask;
  onBack: () => void;
  onMarkCcRead: (id: string) => void;
  onOpenComment: (target: { id: string; author: string; content: string } | null) => void;
  onAction: (code: string) => void;
  sourceForm: ReactNode;
  actionSheet: ReactNode;
  commentSheet: ReactNode;
  commentTotal: number;
  commentsLoading: boolean;
  commentsError?: string;
  commentsPage: number;
  commentsPages: number;
  onCommentsPage: (page: number) => void;
  readBusy: boolean;
  navigationMessage?: string;
};

function actionTone(code: string) {
  if (code === 'APPROVE' || code === 'SUBMIT') return 'blue';
  if (code === 'END' || code === 'RETURN') return 'red';
  if (code === 'RECALL') return 'yellow';
  return 'gray';
}

function MoreIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>;
}

function MobileTaskDetailView(props: Props) {
  const { task } = props;
  const [tab, setTab] = useState<'form' | 'trace' | 'comments'>('form');
  const [moreOpen, setMoreOpen] = useState(false);
  const directActions = task.actions.slice(0, 3);
  const moreActions = task.actions.slice(3);
  const displayedStatus = task.group === 'cc' ? (task.unread ? '抄送未读' : '抄送已读') : task.status;
  const statusTone = task.group === 'done' ? 'green' : task.urgent ? 'red' : 'blue';

  useEffect(() => {
    if (!moreOpen) return;
    const onEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setMoreOpen(false); };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [moreOpen]);

  const openAction = (code: string) => { setMoreOpen(false); props.onAction(code); };
  const openComment = props.onOpenComment;

  return <div className={`mobile-task-detail${task.sourceForm ? ' mobile-task-detail--source-form' : ''}`} aria-busy={!!props.navigationMessage}>
    {props.navigationMessage ? <div className="portal-form-navigation-overlay" role="status">{props.navigationMessage}</div> : null}
    <div className="mobile-task-detail__hero"><button className="mobile-task-detail__back" type="button" aria-label="返回任务列表" onClick={props.onBack}><IconLeft /></button></div>
    <section className="mobile-task-detail__overview" aria-label="流程实例信息">
      <div className="mobile-task-detail__title"><h2>{task.title}</h2><MobileTag tone={statusTone}>{displayedStatus}</MobileTag></div>
      <div className="mobile-task-detail__byline">
        <MobileUserChip name={task.initiator} />
        <span>发起于 <strong>{task.startedAt}</strong></span>
        <span>来源于 <strong>{task.source}</strong></span>
      </div>
    </section>
    <nav className="mobile-detail-tabs" aria-label="任务详情内容"><button className={tab === 'form' ? 'is-active' : ''} type="button" onClick={() => setTab('form')}>表单详情</button><button className={tab === 'trace' ? 'is-active' : ''} type="button" onClick={() => setTab('trace')}>处理轨迹</button><button className={tab === 'comments' ? 'is-active' : ''} type="button" onClick={() => setTab('comments')}>评论 {props.commentTotal}</button></nav>

    {task.sourceForm ? <div className="mobile-task-source-pane" hidden={tab !== 'form'}>{props.sourceForm}</div> : null}
    {tab === 'form' ? <div className="mobile-detail-content">
      {task.sections.map((section) => <section className="mobile-detail-section" key={section.title}><h3>{section.title}</h3><dl>{section.fields.map((field) => <div key={`${section.title}-${field.label}`}><dt>{field.label}</dt><dd>{field.value}</dd></div>)}</dl></section>)}
      <section className="mobile-detail-section"><h3>关联对象</h3><div className="mobile-related"><strong>{task.objectName}</strong><span>{task.objectCode}</span></div></section>
      {task.attachments.length ? <section className="mobile-detail-section"><h3>附件</h3><div className="mobile-attachment-grid">{task.attachments.map((file, index) => <MobileAttachment key={file.fileAssetPublicId ?? index} {...file} />)}</div></section> : null}
    </div> : null}

    {tab === 'trace' ? <div className="mobile-detail-content"><section className="mobile-detail-section"><h3>{task.driver === 'process' ? '流程轨迹' : '状态变化'}</h3><div className="mobile-timeline">{task.trace.map((node) => <article className={`mobile-timeline__item mobile-timeline__item--${node.status}`} key={node.id}>
      <span className="mobile-timeline__dot" aria-hidden="true" />
      <div className="mobile-timeline__node-title">{node.name}</div>
      <div className="mobile-timeline__header">
        <span className="mobile-timeline__avatar" aria-hidden="true">{node.actor.slice(0, 1)}</span>
        <div className="mobile-timeline__identity"><strong>{node.actor}</strong><span className={`mobile-timeline__action-tag mobile-timeline__action-tag--${node.status}`}>{node.action}</span></div>
        <time>{node.time}</time>
      </div>
      <div className="mobile-timeline__details">
        {node.opinion ? <p className="mobile-timeline__opinion">{node.opinion}</p> : null}
        {node.attachments?.length ? <div className="mobile-attachment-grid">{node.attachments.map((file, index) => <MobileAttachment key={file.fileAssetPublicId ?? index} {...file} />)}</div> : null}
      </div>
    </article>)}</div></section></div> : null}

    {tab === 'comments' ? <div className="mobile-detail-content"><section className="mobile-detail-section"><h3>评论与协作</h3>
      {task.comments.length ? <div className="mobile-comment-list">{task.comments.map((item) => <article className="mobile-comment" key={item.id}>
        <div className="mobile-comment__head"><MobileUserChip name={item.author} /><time>{item.time}</time></div>
        {item.replyTo ? <div className="mobile-comment__reply-to">回复 {item.replyTo.author}：{item.replyTo.content}</div> : null}
        <p>{item.content}</p>
        <button className="mobile-comment__reply-button" type="button" onClick={() => openComment({ id: item.id, author: item.author, content: item.content })}>回复</button>
      </article>)}</div> : <div className="mobile-empty">暂无评论</div>}
      <div className="mobile-comment-create"><button type="button" onClick={() => openComment(null)}>发表评论</button></div>
      {props.commentsLoading ? <p role="status">正在加载评论</p> : null}
      {props.commentsError ? <div role="alert">{props.commentsError}<button type="button" onClick={() => props.onCommentsPage(props.commentsPage)}>重试</button></div> : null}
      {props.commentsPages > 1 ? <div className="mobile-comment-pagination"><button type="button" disabled={props.commentsLoading || props.commentsPage <= 1} onClick={() => props.onCommentsPage(props.commentsPage - 1)}>上一页评论</button><span>{props.commentsPage} / {props.commentsPages}</span><button type="button" disabled={props.commentsLoading || props.commentsPage >= props.commentsPages} onClick={() => props.onCommentsPage(props.commentsPage + 1)}>下一页评论</button></div> : null}
    </section></div> : null}

    {(task.group === 'cc' || task.actions.length) ? <div className="mobile-task-actions">
      {task.group === 'cc' ? <button className="mobile-task-action mobile-task-action--gray" type="button" disabled={!task.unread || props.readBusy} onClick={() => props.onMarkCcRead(task.id)}><MobileApprovalActionIcon code="mark-read" /><span>{task.unread ? '标记已读' : '已读'}</span></button> : directActions.map((item) => <button key={item.code} className={`mobile-task-action mobile-task-action--${actionTone(item.code)}`} type="button" disabled={item.disabled} onClick={() => openAction(item.code)}><MobileApprovalActionIcon code={item.code} /><span>{item.label}</span></button>)}
      {moreActions.length ? <button className="mobile-task-more-trigger" type="button" aria-label={`更多操作，${moreActions.length}项`} onClick={() => setMoreOpen(true)}><MoreIcon /><span>更多</span></button> : null}
    </div> : null}
    {moreOpen ? <div className="mobile-task-more" role="dialog" aria-modal="true" aria-label="更多操作">
      <button className="mobile-task-more__mask" type="button" aria-label="关闭更多操作" onClick={() => setMoreOpen(false)} />
      <div className="mobile-task-more__sheet">
        <div className="mobile-task-more__head"><strong>更多操作</strong><button type="button" onClick={() => setMoreOpen(false)}>关闭</button></div>
        <div className="mobile-task-more__list">{moreActions.map((item) => <button className={`mobile-task-more__item mobile-task-more__item--${actionTone(item.code)}`} type="button" key={item.code} disabled={item.disabled} onClick={() => openAction(item.code)}><MobileApprovalActionIcon code={item.code} /><span>{item.label}</span></button>)}</div>
      </div>
    </div> : null}
    {props.actionSheet}
    {props.commentSheet}
  </div>;
}

export default MobileTaskDetailView;
